import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import { useQuery, useQueryClient } from "react-query";
import moment from "moment";
import { BsClock, BsCalendarCheck, BsCheckCircle } from "react-icons/bs";
import { IoPlayOutline, IoPersonOutline } from "react-icons/io5";
import { FaStethoscope } from "react-icons/fa";
import { RxPerson } from "react-icons/rx";
import zoomIcon from "../../assets/images/zoom.svg";
import { apiAdmin, getApiErrorMessage } from "../../services/api";
import { _imagesURL } from "../../constants/API";
import { Language } from "../../App";
import { resolveMentorImage, DEFAULT_MENTOR_AVATAR as placeholderAvatar } from "../../utils/mentorshipUtils";
import { isImageCached, markImageLoaded, preloadImages } from "../../utils/imageCache";

// `label` here is only the English fallback — TAB_LABELS/ROLE_LABELS
// (computed inside the component, where `lang` is available) are what
// actually render, falling back to these if a translation is missing.
const TABS = [
  { key: "active", label: "Active" },
  { key: "completed", label: "Completed" },
];

const ROLES = [
  { key: "mentor", label: "As Mentor", icon: FaStethoscope },
  { key: "mentee", label: "As Mentee", icon: RxPerson },
];

// This whole "As Mentor / As Mentee" switch is only meaningful once the
// doctor is an actual approved mentor — real status from the login API
// (Login.jsx sets this from userData.mentor_profile: null == mentee, an
// object == approved mentor).
function isMentor() {
  return localStorage.getItem("mr_is_mentor") === "true";
}

// A function (not a module-level constant) so it can be rebuilt with the
// current language's translations whenever `lang` changes.
function getEmptyStateCopy(lang) {
  return {
    mentor: {
      active: {
        title: lang?.mentorship_no_active_mentorships_title || "No Active Mentorships",
        subtitle: lang?.mentorship_no_active_mentorships_subtitle || "You don't have any active mentorships. Explore mentors and book your first session.",
      },
      completed: {
        title: lang?.mentorship_no_completed_mentorships_title || "No Completed Mentorships",
        subtitle: lang?.mentorship_no_completed_mentorships_subtitle || "Your completed mentorships will appear here once your sessions have finished.",
      },
    },
    mentee: {
      active: {
        title: lang?.mentorship_no_active_requests_title || "No Active Requests",
        subtitle: lang?.mentorship_no_active_requests_subtitle || "You haven't requested mentorship from anyone yet. Browse mentors to get started.",
      },
      completed: {
        title: lang?.mentorship_no_completed_sessions_title || "No Completed Sessions",
        subtitle: lang?.mentorship_no_completed_sessions_subtitle || "Sessions you've completed as a mentee will appear here.",
      },
    },
  };
}

const STATUS_META = {
  "Awaiting Mentor Response": { color: "#F86500", bg: "#F865001A", icon: BsClock },
  "Sessions Confirmed": { color: "#00BADA", bg: "#00BADA1A", icon: BsCalendarCheck },
  "Mentorship in Progress": { color: "#15803D", bg: "#14B46A1A", icon: IoPlayOutline },
};

// request.status stays a fixed internal key (used for STATUS_META lookup and
// the schedulable/reschedule checks below) — only the label shown to the
// doctor changes depending on which side of the request they're on: on the
// mentor tab they're the one being awaited on ("Awaiting Your Response"),
// everywhere else (mentee tab, or the first-login state before the
// mentor/mentee switch even exists) they're the one waiting on the mentor.
// `status` is always the internal English key (also used for STATUS_META
// lookups and the isSchedulable/completion checks elsewhere in this file,
// and passed straight through to MentorShipSchedule.jsx) — only this
// function's *return value* is translated for display, the key itself
// never changes language.
function getStatusLabel(status, role, lang) {
  if (status === "Awaiting Mentor Response") {
    return role === "mentor"
      ? lang?.mentorship_awaiting_your_response || "Awaiting Your Response"
      : lang?.mentorship_awaiting_mentor_response || "Awaiting Mentor Response";
  }
  if (status === "Sessions Confirmed") {
    return lang?.mentorship_sessions_confirmed_status || "Sessions Confirmed";
  }
  if (status === "Mentorship in Progress") {
    return lang?.mentorship_in_progress_status || "Mentorship in Progress";
  }
  return status;
}

// In-memory module cache for mentorship activity responses (resets on page refresh)
const activityMemoryCache = {};

// Clean up any old persistent activity cache from sessionStorage/localStorage
try {
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith("mr_activity_cache_"))
    .forEach((k) => sessionStorage.removeItem(k));
  Object.keys(localStorage)
    .filter((k) => k.startsWith("mr_activity_cache_"))
    .forEach((k) => localStorage.removeItem(k));
} catch (e) {}

function getUserId() {
  try {
    return JSON.parse(localStorage.getItem("SignupData"))?.data?.id || "anon";
  } catch (e) {
    return "anon";
  }
}

function getActivityCacheKey(role, status) {
  const uid = getUserId();
  return `mr_activity_cache_${uid}_${role || "mentor"}_${status || "active"}`;
}

export function getCachedActivity(role, status) {
  const key = getActivityCacheKey(role, status);
  const list = activityMemoryCache[key];
  if (Array.isArray(list)) {
    try {
      const urls = list
        .map((item) => {
          const counterpart = role === "mentor" ? item.user : item.mentor;
          return resolveMentorImage(counterpart);
        })
        .filter(Boolean);
      preloadImages(urls, true);
    } catch (e) {}
    return list;
  }
  return null;
}

export function setCachedActivity(role, status, list) {
  if (!Array.isArray(list)) return;
  const key = getActivityCacheKey(role, status);
  activityMemoryCache[key] = list;
  try {
    const urls = list
      .map((item) => {
        const counterpart = role === "mentor" ? item.user : item.mentor;
        return resolveMentorImage(counterpart);
      })
      .filter(Boolean);
    preloadImages(urls, true);
  } catch (e) {}
}

// apiAdmin already attaches the Bearer token automatically (see
// src/services/api.js interceptor). `type` is "mentor" or "mentee" (which
// side of the request the doctor is viewing) and `status` is "active" or
// "completed" — the API buckets requests server-side by both.
async function fetchMentorshipActivity({ type, status, lang }) {
  const res = await apiAdmin.post("user/mentor/activity", { type, status });
  if (!res.data?.status) {
    toast.error(getApiErrorMessage(res.data, lang?.mentorship_load_requests_error || "Couldn't load mentorship requests. Please try again."));
    return [];
  }
  const items = res.data?.data?.data || [];
  setCachedActivity(type, status, items);
  return items;
}

// Marks a live session as completed once its scheduled duration has actually
// elapsed (start_timestamp + duration minutes <= now) — called automatically
// by the timer in MentorshipRequests below, not from a button.
async function completeMentorship(requestId) {
  const token = JSON.parse(localStorage.getItem("SignupData") || "null")?.data?.token;
  return apiAdmin.post(
    "user/mentor/complete",
    { request_id: requestId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

function getSessionMoment(activity) {
  if (activity.start_timestamp) {
    const m = moment.unix(Number(activity.start_timestamp));
    if (m.isValid()) return m;
  }
  if (activity.start_date && activity.start_time) {
    const m = moment(
      `${activity.start_date} ${activity.start_time}`,
      ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD h:mm A"]
    );
    if (m.isValid()) return m;
  }
  return null;
}

// activity.status is the API's own enum: 0 = awaiting the mentor's
// response, 1 = session scheduled/confirmed, 2 = session currently live,
// 3 = completed — checked directly instead of guessed from which
// schedule fields happen to be filled in.
const ACTIVITY_STATUS_LABELS = {
  0: "Awaiting Mentor Response",
  1: "Sessions Confirmed",
  2: "Mentorship in Progress",
};

function formatActiveMeta(activity, statusLabel, lang) {
  if (statusLabel === "Mentorship in Progress") {
    // activity.time is NOT the moment the session went live — it stays
    // fixed at whenever the request was first created (confirmed against
    // real data: it was earlier than start_timestamp and matched created_at,
    // not updated_at), which made this show a bogus, way-too-large elapsed
    // time. start_timestamp (the actual scheduled/started moment) is the
    // correct source.
    const sessionMoment = getSessionMoment(activity);
    const minutesAgo = sessionMoment ? Math.max(moment().diff(sessionMoment, "minutes"), 0) : 0;
    const template = lang?.mentorship_started_ago_template || "Started {{time}}";
    let timeStr;
    if (minutesAgo > 0) {
      const unit = minutesAgo === 1 ? lang?.min || "min" : lang?.mins || "mins";
      const agoTemplate = lang?.mentorship_time_ago_template || "{{time}} ago";
      timeStr = agoTemplate.replace("{{time}}", `${minutesAgo} ${unit}`);
    } else {
      timeStr = lang?.just_now || "Just now";
    }
    return template.replace("{{time}}", timeStr);
  }
  if (statusLabel === "Sessions Confirmed") {
    const sessionMoment = getSessionMoment(activity);
    const tz = activity.timezone ? ` (${activity.timezone})` : "";
    return sessionMoment
      ? `${sessionMoment.format("MMM D, YYYY")} - ${sessionMoment.format("h:mm A")}${tz}`
      : lang?.mentorship_session_scheduled_fallback || "Session scheduled";
  }
  const requested = moment(activity.created_at);
  if (!requested.isValid()) return "";
  const prefix = lang?.mentorship_requested_on_prefix || "Requested on";
  return `${prefix} ${requested.format("MMM D, YYYY")}`;
}

// activity.duration is already plain minutes ("90") — show it as-is instead
// of converting to hours, so it matches what the API actually sent.
function formatDuration(minutesValue, lang) {
  const minutes = Number(minutesValue) || 0;
  if (!minutes) return "";
  return `${minutes} ${lang?.min || "min"}`;
}

// activity.data is the CV's storage path ("/uploads/user/410/mentor/xxx.pdf")
// when the mentee attached one, or "" when they didn't — just the last path
// segment is shown as the file name.
function getCvFileName(path) {
  if (!path) return "";
  const segments = path.split("/").filter(Boolean);
  return segments[segments.length - 1] || "";
}

// On the "mentor" tab the counterpart is whoever requested the mentorship
// (activity.user); on the "mentee" tab it's the mentor being requested
// (activity.mentor) — same activity row, different side of it shown.
function mapActivity(activity, role, lang) {
  const counterpart = role === "mentor" ? activity.user : activity.mentor;
  const image = resolveMentorImage(counterpart);
  const name = counterpart?.name || "";
  // Used by MentorShipSchedule's "Go to Profile" button — /profile-doctor/:id.
  const profileId = counterpart?.id;
  // "" (no CV attached) must stay falsy all the way through so the PDF row
  // in MentorShipSchedule can just check `mentee.cvUrl`.
  const cvUrl = activity.data ? `${_imagesURL}${activity.data}` : null;
  const cvFileName = getCvFileName(activity.data);
  const statusCode = Number(activity.status);

  // status 3 means completed regardless of which tab this row came back
  // under — the completed section is keyed off the row itself, not the
  // tab that happened to fetch it.
  if (statusCode === 3) {
    const completed = moment(activity.updated_at);
    return {
      id: activity.id,
      name,
      image,
      profileId,
      cvUrl,
      cvFileName,
      isCompleted: true,
      duration: formatDuration(activity.duration, lang),
      completedDate: completed.isValid() ? completed.format("MMM D, YYYY") : "",
    };
  }

  const status = ACTIVITY_STATUS_LABELS[statusCode] || "Awaiting Mentor Response";
  return {
    id: activity.id,
    name,
    image,
    profileId,
    cvUrl,
    cvFileName,
    isCompleted: false,
    status,
    statusCount: activity.unread_count > 0 ? activity.unread_count : undefined,
    meta: formatActiveMeta(activity, status, lang),
    // Raw scheduling fields — MentorShipSchedule.jsx's reschedule view
    // prefills from these directly instead of re-parsing the formatted
    // `meta` string, so it shows what was actually saved, not a guess.
    startDate: activity.start_date || "",
    startTime: activity.start_time || "",
    startTimestamp: activity.start_timestamp || "",
    durationMinutes: activity.duration || "",
    timezone: activity.timezone || "",
    // The API sends two separate Zoom link fields (meeting_url,
    // meeting_host_url) and only one of them actually comes back populated
    // depending on the session — MentorShipSchedule.jsx's Join button just
    // needs whichever one is actually set.
    meetingUrl: activity.meeting_url || activity.meeting_host_url || "",
  };
}

function RequestItemShimmer() {
  return (
    <div
      className="d-flex align-items-center gap-2 p-2 mb-2"
      style={{ border: "0.5px solid #D7E0EA", borderRadius: "16px" }}
    >
      <div
        className="mr-shimmer-block flex-shrink-0"
        style={{ width: "52px", height: "52px", borderRadius: "50%" }}
      />
      <div className="flex-grow-1">
        <div className="mr-shimmer-block" style={{ width: "40%", height: "13px", borderRadius: "4px", marginBottom: "8px" }} />
        <div className="mr-shimmer-block" style={{ width: "65%", height: "20px", borderRadius: "999px", marginBottom: "6px" }} />
        <div className="mr-shimmer-block" style={{ width: "50%", height: "10px", borderRadius: "4px" }} />
      </div>
    </div>
  );
}

function RequestItem({ request, isCompleted, onSchedule, role }) {
  const { lang } = useContext(Language);
  const navigate = useNavigate();
  const [avatarLoaded, setAvatarLoaded] = useState(() => isImageCached(request.image));
  const imgRef = useRef(null);

  useEffect(() => {
    if (isImageCached(request.image)) {
      setAvatarLoaded(true);
    } else if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      markImageLoaded(request.image);
      setAvatarLoaded(true);
    } else {
      setAvatarLoaded(false);
    }
  }, [request.image]);

  const meta = STATUS_META[request.status] || { color: "#a2a2a2", bg: "#f2f2f2", icon: BsClock };
  const StatusIcon = meta.icon;
  // "Awaiting Mentor Response" opens the modal to schedule a new session;
  // "Sessions Confirmed" opens the same modal in its reschedule view;
  // "Mentorship in Progress" opens it too, but read-only (no reschedule
  // button — see MentorShipSchedule.jsx's isInProgress) so the mentor can
  // still get to the session's Zoom link/details for an already-live call.
  const isSchedulable =
    role === "mentor" &&
    !isCompleted &&
    (request.status === "Awaiting Mentor Response" ||
      request.status === "Sessions Confirmed" ||
      request.status === "Mentorship in Progress");

  // Mentee side of an active request: no Zoom link yet (mentor hasn't
  // scheduled/started the session) sends them to chat with the mentor
  // instead — same navigate(CHAT, {state}) shape MentorShipSchedule.jsx's
  // own message button uses. Once a link exists (Sessions Confirmed or
  // Mentorship in Progress), the whole row opens it directly.
  const isMenteeActive = role === "mentee" && !isCompleted;
  const handleMenteeRowClick = () => {
    if (request.meetingUrl) {
      window.open(request.meetingUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (!request.profileId) return;
    const loginId = JSON.parse(localStorage.getItem("SplashData") || "null")?.user?.id;
    navigate(CHAT, {
      state: {
        from: loginId,
        to: request.profileId,
        group_id: 0,
        user: {
          user_id: request.profileId,
          name: request.name,
          image: "",
        },
      },
    });
  };

  const isClickable = isSchedulable || isMenteeActive;
  const handleRowClick = isSchedulable
    ? () => onSchedule?.(request)
    : isMenteeActive
      ? handleMenteeRowClick
      : undefined;

  return (
    <div
      onClick={handleRowClick}
      className="d-flex align-items-center gap-2 p-2 mb-2"
      style={{
        border: "0.5px solid #D7E0EA",
        borderRadius: "16px",
        cursor: isClickable ? "pointer" : "default",
      }}
    >
      <div
        className="position-relative d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: "52px", height: "52px", borderRadius: "50%", background: "#F7FDFF", overflow: "hidden" }}
      >
        {!avatarLoaded && <Spinner size="sm" style={{ color: "#1dc1dd" }} />}
        <img
          ref={imgRef}
          src={request.image}
          alt={request.name}
          loading="eager"
          decoding="async"
          onLoad={() => {
            markImageLoaded(request.image);
            setAvatarLoaded(true);
          }}
          onError={(e) => {
            markImageLoaded(request.image);
            setAvatarLoaded(true);
            if (e.target.src !== placeholderAvatar) {
              e.target.src = placeholderAvatar;
            }
          }}
          className="rounded-circle object-cover"
          style={{
            position: "absolute",
            inset: 0,
            width: "52px",
            height: "52px",
            opacity: avatarLoaded ? 1 : 0,
            transition: avatarLoaded ? "none" : "opacity 0.25s ease",
          }}
        />
      </div>
      <div className="flex-grow-1 overflow-hidden">
        <span
          className="fs-14 text-truncate d-block"
          style={{ color: "#00BADA", fontWeight: "500" }}
        >
          {request.name}
        </span>

        {isCompleted ? (
          <>
            <span
              className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 my-1 fs-11"
              style={{ color: "#6D7683", background: "#AFAFAF1A", fontWeight: "400" }}
            >
              <BsCheckCircle size={10} />
              {lang?.completed || "Completed"}
            </span>
            <p className="fs-10 roboto-regular mb-0" style={{ color: "#5F6673" }}>
              {lang?.mentorship_duration_label || "Duration"}: {request.duration} &nbsp;•&nbsp; {lang?.completed || "Completed"}: {request.completedDate}
            </p>
          </>
        ) : (
          <>
            <span
              className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 my-1 fs-11"
              style={{ color: meta.color, background: meta.bg, fontWeight: "400" }}
            >
              <StatusIcon size={10} />
              {getStatusLabel(request.status, role, lang)}
            </span>
            <p className="fs-10 roboto-regular mb-0" style={{ color: "#5F6673" }}>
              {request.meta}
            </p>
          </>
        )}
      </div>

      {!isCompleted && request.status === "Mentorship in Progress" ? (
        <img
          src={zoomIcon}
          alt=""
          width={24}
          height={24}
          className="flex-shrink-0"
          onClick={(e) => {
            // stopPropagation matters for the mentor side — the row itself
            // is isSchedulable there (opens the schedule modal), so without
            // this the click would bubble up and open that instead of the
            // Zoom link.
            e.stopPropagation();
            if (!request.meetingUrl) {
              toast.error(lang?.mentorship_zoom_link_unavailable || "The Zoom link isn't available yet.");
              return;
            }
            window.open(request.meetingUrl, "_blank", "noopener,noreferrer");
          }}
          style={{ cursor: "pointer" }}
        />
      ) : !isCompleted && request.statusCount ? (
        <span
          className="d-flex align-items-center justify-content-center text-white fs-12 roboto-bold flex-shrink-0"
          style={{
            height: "20px",
            // Explicit width tiers by digit count — 1 digit stays a perfect
            // 20px circle, 2+ and 3+ digits step up to fixed pill widths
            // instead of trying to infer it from padding/minWidth.
            width:
              request.statusCount >= 100
                ? "46px"
                : request.statusCount >= 10
                  ? "28px"
                  : "20px",
            borderRadius: "999px",
            background: "#e53935",
            boxSizing: "border-box",
            lineHeight: "20px",
          }}
        >
          {request.statusCount}
        </span>
      ) : null}
    </div>
  );
}

function MentorshipRequests({ onScheduleSession }) {
  const { lang } = useContext(Language);
  const [activeTab, setActiveTab] = useState("active");
  const [activeRole, setActiveRole] = useState("mentor");

  // MentorshipStats.jsx's New requests/Scheduled/Completed cards are the
  // mentor's own dashboard numbers — clicking one jumps this panel to the
  // matching tab (and the mentor-side view, since that's what those counts
  // describe). A window event instead of a prop because this component
  // mounts twice at once (desktop sidebar + mobile layout in Mentorship.jsx,
  // toggled with CSS display, not conditional rendering) — both instances
  // need to react, not just whichever one a prop happened to reach.
  useEffect(() => {
    const handleTabRequest = (e) => {
      const tab = e.detail?.tab;
      if (tab === "active" || tab === "completed") {
        setActiveRole("mentor");
        setActiveTab(tab);
      }
    };
    window.addEventListener("mrMentorshipTabRequested", handleTabRequest);
    return () => window.removeEventListener("mrMentorshipTabRequested", handleTabRequest);
  }, []);

  // isMentor() reads localStorage fresh every render, but a plain
  // localStorage write (services/api.js's syncMentorApprovalFlag, which can
  // resolve after this component has already mounted/rendered) doesn't
  // itself trigger a re-render — this tick just forces one when that
  // happens, so a mid-session approval actually reveals the mentor/mentee
  // switch without needing a manual page reload.
  const [, forceMentorTick] = useState(0);
  useEffect(() => {
    const handleSync = () => forceMentorTick((n) => n + 1);
    window.addEventListener("mrMentorStatusSynced", handleSync);
    return () => window.removeEventListener("mrMentorStatusSynced", handleSync);
  }, []);

  const showRoleSwitch = isMentor();
  // Before the mentor/mentee switch even exists (first-ever login) the
  // doctor is effectively still just a mentee, regardless of which data
  // bucket is being shown by default — so the label always reads as mentee
  // in that case, no matter what `activeRole` defaults to internally.
  const labelRole = showRoleSwitch ? activeRole : "mentee";

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["mentorshipActivity", labelRole, activeTab],
    queryFn: () => fetchMentorshipActivity({ type: labelRole, status: activeTab, lang }),
    initialData: () => {
      const cached = getCachedActivity(labelRole, activeTab);
      return Array.isArray(cached) ? cached : undefined;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    onError: (err) =>
      toast.error(getApiErrorMessage(err?.response?.data, lang?.mentorship_load_requests_error || "Couldn't load mentorship requests. Please try again.")),
  });

  const requests = useMemo(
    () => (data || []).map((activity) => mapActivity(activity, labelRole, lang)),
    [data, labelRole, lang]
  );

  // Preload and decode all request avatars so they appear without flicker
  useEffect(() => {
    if (requests.length > 0) {
      const urls = requests.map((r) => r.image).filter(Boolean);
      preloadImages(urls, true);
    }
  }, [requests]);

  // Shimmer only renders when there is no cached/loaded data available yet.
  // When data is already in cache (including an empty list []), requests/empty state render
  // immediately without shimmer, and update quietly once background revalidation finishes.
  const showShimmer = !Array.isArray(data) && (isLoading || isFetching);

  // Only the first 3 requests show by default — "See More" reveals the
  // rest. Resets back to 3 whenever the tab/role changes so switching to
  // e.g. Completed doesn't start already-expanded from the previous list.
  const REQUESTS_PAGE_SIZE = 3;
  const [visibleCount, setVisibleCount] = useState(REQUESTS_PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(REQUESTS_PAGE_SIZE);
  }, [labelRole, activeTab]);
  const visibleRequests = requests.slice(0, visibleCount);
  const hasMoreRequests = requests.length > visibleCount;

  const queryClient = useQueryClient();
  // Tracks request ids we've already fired the complete call for, so a slow
  // refetch (which is what actually removes the row from "in progress")
  // can't cause the same session to be completed twice.
  const completingRef = useRef(new Set());

  // Every 30s, check active sessions. If user hasn't manually completed the session
  // and more than 2 hours (7200 seconds) have passed since the session end time,
  // hit user/mentor/complete automatically.
  useEffect(() => {
    const checkForCompletions = () => {
      const nowSeconds = Date.now() / 1000;
      requests
        .filter(
          (request) =>
            !request.isCompleted &&
            (request.status === "Mentorship in Progress" || request.status === "Sessions Confirmed")
        )
        .forEach((request) => {
          let start = Number(request.startTimestamp);
          if (!start && request.startDate && request.startTime) {
            const m = moment(
              `${request.startDate} ${request.startTime}`,
              ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD h:mm A"]
            );
            if (m.isValid()) start = m.unix();
          }
          const durationMinutes = Number(request.durationMinutes);
          if (!start || !durationMinutes) return;

          const endSeconds = start + durationMinutes * 60;
          // Auto-complete only if 2 hours (7200s) have passed since endSeconds
          const twoHoursInSeconds = 2 * 60 * 60;
          if (nowSeconds < endSeconds + twoHoursInSeconds) return;
          if (completingRef.current.has(request.id)) return;

          completingRef.current.add(request.id);
          completeMentorship(request.id)
            .then(() => queryClient.invalidateQueries(["mentorshipActivity"]))
            .catch((err) => {
              completingRef.current.delete(request.id);
              console.error("Couldn't auto-complete mentorship session after 2 hours:", err);
            });
        });
    };

    checkForCompletions();
    const interval = setInterval(checkForCompletions, 30000);
    return () => clearInterval(interval);
  }, [requests, queryClient]);

  const roleLabels = {
    mentor: lang?.mentorship_as_mentor_label || "As Mentor",
    mentee: lang?.mentorship_as_mentee_label || "As Mentee",
  };
  const tabLabels = {
    active: lang?.active || "Active",
    completed: lang?.completed || "Completed",
  };
  const emptyStateCopy = getEmptyStateCopy(lang);

  return (
    <>
      <style>{`
        .mr-shimmer-block {
          background: linear-gradient(to left, #f3f3f3 25%, #e0e0e0 37%, #f3f3f3 63%);
          background-size: 400% 100%;
          animation: mr-shimmer 5s linear infinite;
        }
        @keyframes mr-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {showRoleSwitch && (
        <div
          className="d-flex mb-3"
          style={{ background: "#fff", borderRadius: "12px", border:"1px solid #D7E0EA", padding: "4px", gap: "4px" }}
        >
          {ROLES.map((role) => {
            const RoleIcon = role.icon;
            const isActiveRole = activeRole === role.key;
            return (
              <button
                key={role.key}
                type="button"
                onClick={() => setActiveRole(role.key)}
                className="flex-fill d-flex align-items-center justify-content-center gap-2 border-0"
                style={{
                  borderRadius: "10px",
                  padding: "9px 0",
                  background: isActiveRole ? "#0E1421" : "transparent",
                  color: isActiveRole ? "#ffffff" : "#5F6673",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                <RoleIcon size={16} />
                {roleLabels[role.key] || role.label}
              </button>
            );
          })}
        </div>
      )}

      <div
        className="bg-white p-3 mb-3"
        style={{
          border: "1px solid #D7E0EA",
          borderRadius: "20px",
        }}
      >
      <div className="d-flex mb-3" style={{ borderBottom: "1px solid #E4E4E7" }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className="btn flex-fill d-flex justify-content-center rounded-0 px-0"
            style={{
              border: "none",
              borderBottom:
                activeTab === tab.key ? "1px solid #00BADA" : "1px solid transparent",
              marginBottom: "-1px",
              background: "transparent",
            }}
          >
            <span
              className="fs-14 pb-2"
              style={{
                color: activeTab === tab.key ? "#131722" : "#8a93a1",
              }}
            >
              {tabLabels[tab.key] || tab.label}
            </span>
          </button>
        ))}
      </div>

      <div style={{ maxHeight: "none", overflowY: "visible" }}>
        {showShimmer ? (
          [1, 2, 3].map((n) => <RequestItemShimmer key={n} />)
        ) : requests.length ? (
          <>
            {visibleRequests.map((request) => (
              <RequestItem
                key={request.id}
                request={request}
                isCompleted={request.isCompleted}
                onSchedule={onScheduleSession}
                role={labelRole}
              />
            ))}
            {hasMoreRequests && (
              <button
                type="button"
                onClick={() => setVisibleCount(requests.length)}
                className="d-block w-100"
                style={{
                  background: "#1DC1DD1A",
                  color: "#00BADA",
                  fontWeight: 600,
                  fontSize: "13px",
                  border:"1px solid #E6E7EA",
                  borderRadius: "12px",
                padding: "8px 0",
                }}
              >
                {lang?.see_more || "See More"}
              </button>
            )}
          </>
        ) : (
          <div className="text-center" style={{ padding: "56px 16px" }}>
            <p className="fs-14  mb-1" style={{ color: "#8F98A3", fontWeight:"400" }}>
              {emptyStateCopy[labelRole][activeTab].title}
            </p>
            <p
              className="fs-12  mb-0 mx-auto"
              style={{ color: "#8F98A3", lineHeight: "18px", fontWeight:"400", maxWidth: "260px" }}
            >
              {emptyStateCopy[labelRole][activeTab].subtitle}
            </p>
          </div>
        )}
      </div>
      </div>
    </>
  );
}

export default React.memo(MentorshipRequests);